import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { AXIOS } from '@/lib/http-common';
import LogLine from '../../components/log-viewer/model/LogLine';

@Component({})
export default class LogViewer extends Vue {

    public selectMenuOptions = { maxHeight: 'none' };

    public newLogLineInterval!: number;
    public newLogFileInterval!: number;

    public logLevels: string[] = ['INFO', 'DEBUG', 'WARN', 'ERROR', 'TRACE', 'NONE'];
    public activeLogLevels: string[] = ['INFO', 'DEBUG', 'WARN', 'ERROR', 'TRACE', 'NONE'];

    public selectedLogFile: string = '';
    public logFiles: string[] = [];

    public logLines: LogLine[] = [];
    public filter: string = '';

    public loadingLogs: boolean = false;
    public loadingMoreLogs: boolean = false;
    public scrolledDown: boolean = true;
    public scrolledUp: boolean = true;

    public LOAD_AMOUNT_OF_LINES = 100;

    public mounted() {
        this.getLogFiles();
    }

    public initNewLogLinesTimer() {
        // Interval for getting new log lines of the selected file
        this.newLogLineInterval = setInterval(() => {
            const params = {
                fileName: this.selectedLogFile,
                fromLine: this.logLines.length > 0 ? this.logLines[this.logLines.length - 1].lineNumber + 1 : -1,
                maxLines: this.LOAD_AMOUNT_OF_LINES
            };
            AXIOS.get('/log', { params }).then((message) => {
                // Add to end of list
                const logList: LogLine[] = message.data;
                this.logLines.push(...logList);

                // Scroll the list to lowest line if the list was already scrolled to the bottom
                const objDiv = this.$refs.logTableContainer as HTMLElement;
                if (objDiv.offsetHeight + objDiv.scrollTop === objDiv.scrollHeight) {
                    this.$nextTick(() => {
                        this.$vuetify.goTo(objDiv.scrollHeight, { container: objDiv });
                    });
                }
            });
        }, 1000);
    }

    public initNewLogFilesTimer() {
        this.newLogFileInterval = setInterval(() => {
            AXIOS.get('/log/files').then((response) => {
                const files = response.data as string[];
                files.forEach((file) => {
                    if (this.logFiles.indexOf(file) < 0) {
                        this.logFiles.unshift(file);
                    }
                });
            });
        }, 10000);
    }

    public getLogFiles() {
        AXIOS.get('/log/files').then((response) => {
            this.logFiles = response.data;
            this.selectedLogFile = this.logFiles[0];
            this.initNewLogFilesTimer();
        });
    }

    public getLogLines() {
        const params = {
            fileName: this.selectedLogFile,
            maxLines: this.LOAD_AMOUNT_OF_LINES
        };
        this.loadingLogs = true;
        AXIOS.get('/log', { params }).then((message) => {
            // Get new logs and reverse them into the list
            const list: LogLine[] = message.data;
            this.logLines = list;

            // Subscribe to log topic to recieve new line of the selected file
            this.initNewLogLinesTimer();

            // Scroll to last line
            this.$nextTick(() => {
                const objDiv = this.$refs.logTableContainer as HTMLElement;
                objDiv.scrollTop = objDiv.scrollHeight;
            });
        }).finally(() => this.loadingLogs = false);
    }

    public getMoreLogLines() {
        // Get 100 history log lines
        if (!this.loadingMoreLogs) {
            this.loadingMoreLogs = true;
            const params = {
                fileName: this.selectedLogFile,
                fromLine: this.logLines[0].lineNumber - this.LOAD_AMOUNT_OF_LINES,
                maxLines: this.LOAD_AMOUNT_OF_LINES
            };
            AXIOS.get('/log', { params }).then((message) => {
                // Append new lines to the list
                const list: LogLine[] = message.data;
                this.logLines = list.concat(this.logLines);

                // Scroll list to first to the oldest log
                const objDiv = this.$refs.logTableContainer as HTMLElement;
                this.$nextTick(() => {
                    this.$vuetify.goTo(0, { container: objDiv });
                });
            }).finally(() => this.loadingMoreLogs = false);
        }
    }

    public flushLog() {
        this.logLines = [this.logLines[this.logLines.length - 1]];
    }

    public reloadLog() {
        this.logLines = [];
        clearInterval(this.newLogLineInterval);
        this.getLogLines();
    }

    public filterLog(line: LogLine) {
        return this.activeLogLevels.includes(line.level) && line.content.toLowerCase().match(this.filter.toLowerCase());
    }

    public toggleLogLevel(logLevel: string) {
        const index = this.activeLogLevels.indexOf(logLevel);
        if (index > -1) {
            this.activeLogLevels.splice(index, 1);
        } else {
            this.activeLogLevels.push(logLevel);
        }
    }

    @Watch('selectedLogFile')
    public onSelectedLogFileChanged(newFile: string, oldFile: string) {
        clearInterval(this.newLogLineInterval);
        this.logLines.splice(0, this.logLines.length);
        this.getLogLines();
    }

    public scrollUpOrLoad() {
        const objDiv = this.$refs.logTableContainer as HTMLElement;
        if (objDiv.scrollTop === 0) {
            this.getMoreLogLines();
        } else {
            this.$vuetify.goTo(0, { container: objDiv });
        }
    }

    public scrollDown() {
        const objDiv = this.$refs.logTableContainer as HTMLElement;
        this.$vuetify.goTo(objDiv.scrollHeight, { container: objDiv });
    }

    public checkScroll(event: Event) {
        const objDiv = event.target as HTMLElement;
        this.scrolledDown = objDiv.offsetHeight + objDiv.scrollTop === objDiv.scrollHeight;
        this.scrolledUp = objDiv.scrollTop === 0;
    }

    public beforeDestroy() {
        clearInterval(this.newLogLineInterval);
        clearInterval(this.newLogFileInterval);
    }
}
